import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import * as authService from "@/services/authService";
import type { Session, SignupInput } from "@/services/authService";

// Minimal user shape kept compatible with what components already read
// (user.id, user.email, user.user_metadata.full_name).
export interface AppUser {
  id: string;
  email: string;
  user_metadata: { full_name: string; role: string };
}

function sessionToUser(session: Session): AppUser {
  return {
    id: session.id,
    email: session.email,
    user_metadata: { full_name: session.fullName, role: session.role },
  };
}

interface AuthState {
  user: AppUser | null;
  loading: boolean;
  loginStudent: (identifier: string, password: string, rememberMe?: boolean) => Promise<{ error: string | null; role: string | null }>;
  loginAdmin: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: string | null; role: string | null }>;
  signup: (input: SignupInput) => Promise<{ error: string | null; alreadyExists?: boolean }>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  isAdmin: boolean;
  studentId: string | null;
  role: string | null;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  loginStudent: async () => ({ error: "Auth not initialized", role: null }),
  loginAdmin: async () => ({ error: "Auth not initialized", role: null }),
  signup: async () => ({ error: "Auth not initialized" }),
  logout: async () => {},
  refreshSession: async () => false,
  isAdmin: false,
  studentId: null,
  role: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    const session = authService.getSession();
    if (session) {
      setUser(sessionToUser(session));
      setRole(session.role);
    }
    setLoading(false);
  }, []);

  const applySessionAfterLogin = useCallback((result: { error: string | null; role: string | null }) => {
    if (!result.error) {
      const session = authService.getSession();
      if (session) {
        setUser(sessionToUser(session));
        setRole(session.role);
      }
    }
    return result;
  }, []);

  const loginStudent = useCallback(async (identifier: string, password: string, rememberMe = false) => {
    return applySessionAfterLogin(await authService.loginStudent(identifier, password, rememberMe));
  }, [applySessionAfterLogin]);

  const loginAdmin = useCallback(async (email: string, password: string, rememberMe = false) => {
    return applySessionAfterLogin(await authService.loginAdmin(email, password, rememberMe));
  }, [applySessionAfterLogin]);

  const signup = useCallback(async (input: SignupInput) => {
    return authService.signup(input);
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setRole(null);
  }, []);

  const refreshSession = useCallback(async () => {
    const ok = await authService.refreshSession();
    if (!ok) {
      setUser(null);
      setRole(null);
    }
    return ok;
  }, []);

  const isAdmin = role === "admin";
  const studentId = role === "student" ? user?.id ?? null : null;

  return (
    <AuthContext.Provider value={{ user, loading, loginStudent, loginAdmin, signup, logout, refreshSession, isAdmin, studentId, role }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
